/**
 * System configuration and monitoring operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class SystemManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async getSystemInfo(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/info');
  }

  async getServerStatus(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/health');
  }

  async getSystemConfig(key?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    if (key) params.key = key;
    return this.session.get('/api/cdp/system/config', { params });
  }

  async updateSystemConfig(
    key: string,
    value: string,
    description?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = {
      data: { key, value },
    };
    if (description) data.data.description = description;
    return this.session.post('/api/cdp/system/config', { data });
  }

  async getProjectConfig(projectUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/project_config/list', {
      params: { projectUuid },
    });
  }

  async updateProjectConfig(
    projectUuid: string,
    key: string,
    value: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: { projectUuid, key, value },
    };
    return this.session.post('/api/cdp/project_config', { data });
  }

  async getAuditLogs(
    page = 1,
    size = 20,
    startTime?: string,
    endTime?: string,
    operationType?: string,
    resourceType?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = {};
    if (startTime) options.startTime = startTime;
    if (endTime) options.endTime = endTime;
    if (operationType) options.operationType = operationType;
    if (resourceType) options.resourceType = resourceType;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp/log/audit/page', { params });
  }

  async getOperationLogs(
    page = 1,
    size = 20,
    userId?: number,
    startTime?: string,
    endTime?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string | number> = {};
    if (userId !== undefined) options.userId = userId;
    if (startTime) options.startTime = startTime;
    if (endTime) options.endTime = endTime;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp/log/operation/page', { params });
  }

  async getSystemMetrics(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/metrics');
  }

  async getActiveSessions(): Promise<Record<string, any>[]> {
    const result = await this.session.get('/api/cdp/system/sessions');
    return result?.data ?? [];
  }

  async terminateSession(sessionId: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/system/sessions/terminate', {
      data: { sessionId },
    });
  }

  async getSystemResources(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/resources');
  }

  async getCacheStats(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/cache/stats');
  }

  async clearCache(cacheName?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = {};
    if (cacheName) data.cacheName = cacheName;
    return this.session.post('/api/cdp/system/cache/clear', { data });
  }

  async getDataSourceHealth(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/datasource/health');
  }

  async getLicenseInfo(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/license');
  }

  async updateLicense(licenseKey: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/system/license', { data: { licenseKey } });
  }

  async getBackupList(): Promise<Record<string, any>[]> {
    const result = await this.session.get('/api/cdp/system/backup/list');
    return result?.data ?? [];
  }

  async createBackup(
    name: string,
    description?: string,
    includeData = true,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { name, includeData };
    if (description) data.description = description;
    return this.session.post('/api/cdp/system/backup', { data });
  }

  async restoreBackup(backupId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/system/backup/${backupId}/restore`);
  }

  async deleteBackup(backupId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/system/backup/${backupId}`);
  }

  async getSystemNotifications(
    page = 1,
    size = 20,
    unreadOnly = false,
  ): Promise<Record<string, any>> {
    const params: Record<string, any> = { current: page, size };
    if (unreadOnly) params.unreadOnly = 'true';
    return this.session.get('/api/cdp/system/notifications', { params });
  }

  async markNotificationRead(notificationId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/system/notifications/${notificationId}/read`);
  }

  async getMaintenanceStatus(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/system/maintenance');
  }

  async setMaintenanceMode(enabled: boolean, message?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = { enabled };
    if (message) data.message = message;
    return this.session.post('/api/cdp/system/maintenance', { data });
  }

  async getApiLog(
    page = 1,
    size = 20,
    projectUuid?: string,
    logType?: string,
  ): Promise<Record<string, any>> {
    const params: Record<string, any> = { current: page, size };
    if (projectUuid) params.projectUuid = projectUuid;
    if (logType) params.logType = logType;
    return this.session.get('/api/cdp/log/api/page', { params });
  }

  // --- Extended system APIs ---

  async getDiskFile(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/disk/file', { params });
  }

  async publishFolder(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/disk/publishFolder', { data: { context: {}, data } });
  }

  async getWatermarkConfig(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/config/extend/configWatermark');
  }

  async getKeywords(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/system/extend/keywords', { params });
  }

  async getEnumPage(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_enum_type/page2', {
      params: { current: page, size, ...params },
    });
  }

  // --- Operation log APIs ---

  async pageOperationLogs(page = 1, size = 20, options?: Record<string, any>): Promise<Record<string, any>> {
    const params: Record<string, any> = { page, size };
    if (options) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp_operation_log/page', { params });
  }

  async getOperationLog(id: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp_operation_log/get/${id}`);
  }

  async createOperationLog(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_operation_log/create', { data });
  }

  async editOperationLog(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_operation_log/edit', { data });
  }

  async deleteOperationLog(id: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp_operation_log/delete/${id}`);
  }

  async batchDeleteOperationLogs(ids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_operation_log/batchDelete', { data: ids });
  }

  async recordOperateTime(projectUuid: string, data: Record<string, any>[]): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_operation_log/recordOperateTime/${projectUuid}`, { data });
  }
}
